import {type GameValidator} from '@colonist/game-mechanic-validators'
import {type GameChatMessageData, GameChatType, type NestedReadonly} from '@colonist/game-states'
import {LobbyImage} from '@colonist/types'
import {type TranslationObject} from '@colonist/i18n'
import {type FeedMessageData} from '@colonist/shared-types'

export function createFeedMessageDataForGameChatData(gameValidator: GameValidator, gameChatData: NestedReadonly<GameChatMessageData>): FeedMessageData {
    if(gameChatData.type == GameChatType.Message) {
        const senderInformation = gameValidator.getPlayerValidator(gameChatData.from)

        return {
            text: getFeedMessageDataTextForGameChatData(gameChatData),
            icon: senderInformation.userState.databaseIcon,
            username: senderInformation.username,
            usernameColor: senderInformation.color,
            colorAndBoldUsernames: true,
            profilePictureUrl: senderInformation.profilePictureUrl,
            isSenderUser: true,
        }
    }

    return {
        text: getFeedMessageDataTextForGameChatData(gameChatData),
        icon: LobbyImage.Lightning,
        colorAndBoldUsernames: false,
    }
}

function getFeedMessageDataTextForGameChatData(message: NestedReadonly<GameChatMessageData>): TranslationObject {
    switch(message.type) {
        case GameChatType.Message:
        case GameChatType.SystemMessage:
            return {
                key: 'strings:colonistCommon.utils.#buffer',
                options: {
                    value: message.message,
                    interpolation: {escapeValue: false},
                },
            }
        case GameChatType.UnableToSend:
            return {key: 'strings:game.gameState.gameLogs.couldNotsendChatMessage'}
        case GameChatType.ChatDisabled:
            return {key: 'strings:game.gameState.gameLogs.chatIsDisabled'}
        case GameChatType.ChatEnabled:
            return {key: 'strings:commands.messages.chatEnabled'}
        case GameChatType.BadLanguage:
            return {key: 'strings:game.gameState.gameLogs.useProperLanguage'}
        case GameChatType.TooLong:
            return {key: 'strings:game.gameState.gameLogs.messageTooLong'}
        case GameChatType.Spam:
            return {key: 'strings:game.gameState.gameLogs.pleaseDoNotSpam'}
        case GameChatType.WrongCommand:
            return {key: 'strings:commands.errors.wrongCommand'}
        case GameChatType.CommandCrashed:
            return {key: 'strings:commands.errors.commandCrashed'}
        case GameChatType.GameCommandOnly:
            return {key: 'strings:commands.errors.gameCommandOnly'}
        case GameChatType.EmojiTutorial:
            return {
                key: 'strings:commands.messages.emojiTutorial',
                options: {
                    exampleString: message.exampleString,
                    exampleEmojis: message.exampleEmojis,
                }}
        case GameChatType.CommandTutorial:
            return {
                key: 'strings:commands.messages.chatTutorial',
                options: {
                    exampleString: message.exampleString,
                }}
        case GameChatType.DisableKarmaTutorial:
            return {
                key: 'strings:commands.messages.karmaTutorial',
                options: {
                    exampleString: message.exampleString,
                }}
        case GameChatType.PauseGameTutorial:
            return {
                key: 'strings:commands.messages.pauseTutorial',
                options: {
                    exampleString: message.exampleString,
                }}
        case GameChatType.SpectatorCannotSendMessage:
            return {key: 'strings:game.gameState.gameLogs.spectatorCannotSendMessage'}
        case GameChatType.YouAreMuted:
            return {key: 'strings:game.gameState.gameLogs.youHaveBeenMuted', options: {date: message.mutedUntil}}
        case GameChatType.AdminKickedPlayer:
            return {key: 'strings:game.gameState.gameLogs.kickedByAdmin'}
        case GameChatType.CanOnlyResignIn1v1:
            return {key: 'strings:commands.errors.onlyAvailableFor1v1'}
    }
}
